import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { fetchPatients } from "../../core/redux/slices/patientSlice";
import { fetchInvoices } from "../../core/redux/slices/invoiceSlice";
import { getInvoice } from "../../core/api/invoiceService";

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const STATUS_BADGE = { Draft: "secondary", Paid: "success", PartialPaid: "warning", Cancelled: "danger" };

const PatientHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const patients = useSelector((s) => s.patients.list);
  const invoices = useSelector((s) => s.invoices.list);

  const [patientId, setPatientId] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchPatients());
    dispatch(fetchInvoices());
  }, [dispatch]);

  const patientOptions = patients.map((p) => ({
    value: p.patientID || p.id,
    label: `${p.patientName} — ${p.mobileNumber || ""} (OP: ${p.opNumber || "—"})`,
  }));

  const patient = patients.find((p) => (p.patientID || p.id) === patientId);

  // No server-side appointment-history-by-patient endpoint exists (getAppointments only
  // filters by date), so treatment history here is derived from this patient's invoices.
  // The /api/Invoice/all list doesn't reliably include procedure/product lines, so each
  // matching invoice is re-fetched individually for full treatment detail — fine for a
  // single patient's invoice count. Visits that never became an invoice won't show up here.
  useEffect(() => {
    if (!patientId) { setHistory([]); return; }
    let cancelled = false;
    setLoading(true);
    const matches = invoices.filter((inv) => (inv.patientID ?? inv.patientId) === patientId);
    Promise.all(matches.map((inv) => getInvoice(inv.id).then((r) => r.data).catch(() => inv)))
      .then((details) => {
        if (cancelled) return;
        setHistory(details.sort((a, b) => (b.invoiceDate || "").localeCompare(a.invoiceDate || "")));
      })
      .catch(() => { if (!cancelled) Swal.fire("Error", "Failed to load patient history.", "error"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, invoices.length]);

  const totals = history.reduce((t, inv) => ({
    billed: t.billed + Number(inv.netTotal || 0),
    paid: t.paid + Number(inv.paidAmount || 0),
    balance: t.balance + Number(inv.balanceAmount || 0),
  }), { billed: 0, paid: 0, balance: 0 });

  const columns = [
    { title: "Date", dataIndex: "invoiceDate", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { title: "Invoice #", dataIndex: "invoiceNumber", width: 120 },
    { title: "Doctor", dataIndex: "doctorName" },
    {
      title: "Treatment", dataIndex: "procedureLines",
      render: (_, rec) => (rec.procedureLines || []).map((l) => l.procedureName).filter(Boolean).join(", ") || "—",
    },
    { title: "Amount", dataIndex: "netTotal", align: "right", width: 120, render: fmt },
    { title: "Paid", dataIndex: "paidAmount", align: "right", width: 110, render: fmt },
    {
      title: "Balance", dataIndex: "balanceAmount", align: "right", width: 110,
      render: (v) => <span className={v > 0 ? "text-danger fw-semibold" : "text-success"}>{fmt(v)}</span>,
    },
    {
      title: "Status", dataIndex: "invoiceStatus", width: 110,
      render: (v) => <span className={`badge bg-${STATUS_BADGE[v] || "secondary"}`}>{v === "PartialPaid" ? "Partial Paid" : v}</span>,
    },
    {
      title: "Action", dataIndex: "action", width: 90,
      render: (_, rec) => (
        <button className="btn btn-sm btn-outline-primary" onClick={() => navigate(`/invoices/${rec.id}/edit`)}>View</button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Patient History</h4><h6>Full treatment &amp; billing history for a patient</h6></div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <label className="form-label small mb-1">Patient</label>
            <Select
              classNamePrefix="react-select"
              options={patientOptions}
              value={patientOptions.find((o) => o.value === patientId) || null}
              onChange={(opt) => setPatientId(opt?.value || "")}
              placeholder="Search by name or mobile..."
              isClearable
              isSearchable
            />
          </div>
        </div>

        {patient && (
          <>
            <div className="card mb-3">
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3 mb-2"><strong>OP Number:</strong> {patient.opNumber || "—"}</div>
                  <div className="col-md-3 mb-2"><strong>Age / Gender:</strong> {patient.age ?? "—"}/{patient.gender || "—"}</div>
                  <div className="col-md-3 mb-2"><strong>Mobile:</strong> {patient.mobileNumber || "—"}</div>
                  <div className="col-md-3 mb-2"><strong>Category:</strong> {patient.category || "—"}</div>
                  <div className="col-md-6 mb-2"><strong>Address:</strong> {patient.address || "—"}</div>
                  <div className="col-md-6 mb-2"><strong>Allergies:</strong> {patient.allergies || "—"}</div>
                  <div className="col-md-12 mb-0"><strong>Medical History:</strong> {patient.medicalHistory || "—"}</div>
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-6 col-md-4 mb-2">
                <div className="card border-primary h-100"><div className="card-body text-center py-3">
                  <div className="text-primary fw-bold" style={{ fontSize: "1.3rem" }}>{fmt(totals.billed)}</div>
                  <div className="text-muted small mt-1">Total Billed</div>
                </div></div>
              </div>
              <div className="col-6 col-md-4 mb-2">
                <div className="card border-success h-100"><div className="card-body text-center py-3">
                  <div className="text-success fw-bold" style={{ fontSize: "1.3rem" }}>{fmt(totals.paid)}</div>
                  <div className="text-muted small mt-1">Total Paid</div>
                </div></div>
              </div>
              <div className="col-6 col-md-4 mb-2">
                <div className="card border-danger h-100"><div className="card-body text-center py-3">
                  <div className="text-danger fw-bold" style={{ fontSize: "1.3rem" }}>{fmt(totals.balance)}</div>
                  <div className="text-muted small mt-1">Outstanding Balance</div>
                </div></div>
              </div>
            </div>

            <div className="alert alert-warning py-2 px-3 small">
              This history is built from the patient&apos;s invoices only — appointments that never became an invoice won&apos;t
              appear here. Showing appointment-only visits needs a patient-filterable appointment endpoint on the backend.
            </div>

            <div className="card table-list-card">
              <div className="card-body">
                <div className="table-responsive">
                  <Table columns={columns} dataSource={history} loading={loading} />
                </div>
              </div>
            </div>
          </>
        )}

        {!patient && (
          <div className="card text-center py-5 text-muted">
            <p>Select a patient to view their treatment and billing history.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientHistory;
